import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import menus from "../../pages/menu";
import "./styles.scss";
import logo from "../../assets/images/logo/logoinno.png";
import Button from "../button";
import { CONTACT } from "../../lib/contact";
import { getServiceContent, groupedServices, servicePath } from "../../content/services";
import { alternatePath, localeFromPath, servicesRoot } from "../../lib/i18n-routes";

import rus from "../../assets/images/rus.png";
import uz from "../../assets/images/uz.png";

const LANGS = [
  { id: 1, code: "uz", img: uz, name: "UZ" },
  { id: 2, code: "ru", img: rus, name: "RU" },
];

const Header = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const locale = localeFromPath(pathname);

  const [scroll, setScroll] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScroll(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Marshrut o'zgarganda barcha menyular yopiladi
  useEffect(() => {
    setMenuActive(false);
    setServicesOpen(false);
    setLangOpen(false);
  }, [pathname]);

  const groups = groupedServices();
  const current = LANGS.find((l) => l.code === locale) || LANGS[0];
  const home = locale === "ru" ? "/ru" : "/";
  const isHome = pathname === "/" || pathname === "/ru";

  /** Bosh sahifadagi langar havolalar ichki sahifalarda ham ishlashi uchun */
  const anchor = (hash) => (isHome ? hash : `${home}${hash}`);

  /**
   * Til almashtirish — sahifani qayta yuklamaydi.
   * Xizmat sahifasida slug ham almashadi (alternatePath buni hisobga oladi).
   */
  const switchLang = () => {
    setLangOpen(false);
    navigate(alternatePath(pathname, locale));
  };

  return (
    <header id="header_main" className={`header ${scroll ? "is-fixed" : ""}`}>
      <div className="container">
        <div id="site-header-inner">
          <div className="header__logo">
            <NavLink to={home}>
              <img src={logo} className="navlogo" alt="Innobek" />
            </NavLink>
          </div>

          <nav id="main-nav" className={`main-nav ${menuActive ? "active" : ""}`}>
            <ul id="menu-primary-menu" className="menu">
              {/* Xizmatlar — mega menyu */}
              <li
                className={`menu-item menu-item-has-children has-mega ${servicesOpen ? "active" : ""}`}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={servicesRoot(locale)}
                  onClick={(e) => {
                    // Mobil/planshetda birinchi bosish menyuni ochadi
                    if (window.innerWidth <= 1024 && !servicesOpen) {
                      e.preventDefault();
                      setServicesOpen(true);
                    }
                  }}
                >
                  {t("services")}
                </Link>

                <div className={`mega-menu${servicesOpen ? " is-open" : ""}`}>
                  <div className="mega-menu__inner">
                    {groups.map((group) => (
                      <div className="mega-menu__col" key={group.key}>
                        <p className="mega-menu__title">{t(group.i18n)}</p>
                        <ul>
                          {group.items.map((s) => {
                            const c = getServiceContent(s.slug, locale);
                            if (!c) return null;
                            return (
                              <li key={s.slug}>
                                <Link to={servicePath(s, locale)}>
                                  <span className="mega-menu__plus">+</span>
                                  {c.hero.h1}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <Link className="mega-menu__all" to={servicesRoot(locale)}>
                    {t("all-services")} &#8599;
                  </Link>
                </div>
              </li>

              {menus.map((data, idx) => (
                <li key={idx} className="menu-item">
                  <a href={anchor(data.links)}>{t(data.name)}</a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop — matnli tugma */}
          <Button title={t("connect")} path={CONTACT.phoneHref} />

          {/* Mobil — kompakt qo'ng'iroq tugmasi (matn tor ekranda joy yeb qo'yadi) */}
          <a className="header-call" href={CONTACT.phoneHref} aria-label={t("connect")}>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z"
              />
            </svg>
          </a>

          <div className="lang">
            <button
              type="button"
              onClick={() => setLangOpen((s) => !s)}
              aria-expanded={langOpen}
              aria-label={current.name}
            >
              <img src={current.img} alt="" />
              <span>{current.name}</span>
            </button>

            <div className="lang__drop" style={{ display: langOpen ? "block" : "none" }}>
              {LANGS.filter((l) => l.code !== locale).map((item) => (
                <button key={item.id} type="button" onClick={switchLang}>
                  <img src={item.img} alt="" />
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            className={`mobile-button ${menuActive ? "active" : ""}`}
            onClick={() => setMenuActive((s) => !s)}
            role="button"
            tabIndex={0}
            aria-label="Menu"
            onKeyDown={(e) => e.key === "Enter" && setMenuActive((s) => !s)}
          >
            <span></span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
